import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import {
  AppstoreOutlined,
  EditOutlined,
  PlusOutlined,
  StarFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  createProject,
  deleteProject,
  updateProject,
} from "../../../service/project.service";
import { getAllEnvironment } from "../../../service/admin.service";
import { useProject } from "../../../context/ProjectContext";
import PageHeader from "../../../components/generic/PageHeader";
import "../../../styles/db.css";
import "../../../styles/libraryTable.css";

dayjs.extend(customParseFormat);

// Backend stores every date as VARCHAR in this exact format.
const DATE_FORMAT = "DD-MM-YYYY HH:mm:ss";
const DISPLAY_FORMAT = "DD-MM-YYYY";

const toStamp = (d) => (d ? d.format(DATE_FORMAT) : null);
const toDate = (s) => {
  if (!s) return null;
  const d = dayjs(s, DATE_FORMAT);
  return d.isValid() ? d : null;
};
const showDate = (s) => (s ? s.split(" ")[0] : "—");

function Projects() {
  const {
    projects,
    activeProjectId,
    defaultProjectId,
    isAdmin,
    refreshProjects,
    switchProject,
  } = useProject();
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed, {} = create
  const [form] = Form.useForm();

  // Environment names repeat in the library; dedupe so the Select has unique keys.
  const envOptions = useMemo(
    () => [
      ...new Set(
        (environments || []).map((e) => e.displayName).filter(Boolean),
      ),
    ],
    [environments],
  );

  useEffect(() => {
    getAllEnvironment()
      .then((res) => setEnvironments(res.data || []))
      .catch(() => setEnvironments([]));
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      await refreshProjects();
    } catch {
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({});
    form.setFieldsValue({
      name: "",
      startDate: null,
      endDate: null,
      defaultEnvironment: undefined,
      active: true,
    });
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      startDate: toDate(record.startDate),
      endDate: toDate(record.endDate),
      defaultEnvironment: record.defaultEnvironment || undefined,
      active: record.active,
    });
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // AntD renders the field errors
    }

    const payload = {
      name: values.name.trim(),
      startDate: toStamp(values.startDate),
      endDate: toStamp(values.endDate),
      defaultEnvironment: values.defaultEnvironment || null,
      active: values.active,
    };

    setSaving(true);
    try {
      if (editing?.projectId) {
        await updateProject(editing.projectId, payload);
        message.success("Project updated");
      } else {
        await createProject(payload);
        message.success("Project created");
      }
      setEditing(null);
      await reload();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Could not save the project",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteProject(record.projectId);
      message.success("Project deleted");

      // Never leave the session pointing at a project that no longer exists —
      // every request would carry a dead X-Project-Id. redirect:false keeps the
      // admin on this page instead of bouncing them to the Dashboard mid-task.
      if (record.projectId === activeProjectId) {
        switchProject(defaultProjectId || "PRJ-DEFAULT", { redirect: false });
      }
      await reload();
    } catch (error) {
      message.error(
        error.response?.data?.message || "Could not delete the project",
      );
    }
  };

  const columns = [
    {
      title: "Project",
      dataIndex: "name",
      key: "name",
      width: "28%",
      render: (text, record) => (
        <span>
          <span className="arc-library-name">{text}</span>
          {record.projectId === defaultProjectId && (
            <StarFilled
              style={{ color: "#185FA5", fontSize: 11, marginLeft: 8 }}
              title="Your default project"
            />
          )}
        </span>
      ),
    },
    {
      title: "Start date",
      dataIndex: "startDate",
      key: "startDate",
      width: "14%",
      render: showDate,
    },
    {
      title: "End date",
      dataIndex: "endDate",
      key: "endDate",
      width: "14%",
      render: showDate,
    },
    {
      title: "Default environment",
      dataIndex: "defaultEnvironment",
      key: "defaultEnvironment",
      width: "20%",
      render: (text) => text || "—",
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: "10%",
      render: (active) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Options",
      key: "options",
      width: "14%",
      render: (_, record) => (
        <span>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this project?"
            description="Its library, tasks, results and jobs stay in the database but become unreachable."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
            disabled={record.projectId === "PRJ-DEFAULT"}
          >
            <Button
              type="link"
              size="small"
              danger
              disabled={record.projectId === "PRJ-DEFAULT"}
            >
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div className="db-container">
      <PageHeader
        icon={<AppstoreOutlined />}
        title="Projects"
        tooltip="Projects scope the library, tasks, master suites, results and jobs"
        backTo="/admin"
      />

      {!isAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Read-only"
          description="Only an admin can create, edit or delete projects. You can still switch between them from the sidebar."
        />
      )}

      <Row justify="end" style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={!isAdmin}
        >
          New Project
        </Button>
      </Row>

      <Table
        rowKey="projectId"
        columns={columns}
        dataSource={projects}
        loading={loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        wrapClassName="arc-library-table"
      />

      <Modal
        open={editing !== null}
        title={editing?.projectId ? "Edit Project" : "New Project"}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Project name"
            rules={[
              { required: true, message: "Project name is required" },
              { whitespace: true, message: "Project name is required" },
              { max: 200, message: "Project name is too long" },
            ]}
          >
            <Input placeholder="e.g. Client Alpha Migration" />
          </Form.Item>

          <Form.Item name="startDate" label="Start date">
            <DatePicker style={{ width: "100%" }} format={DISPLAY_FORMAT} />
          </Form.Item>

          <Form.Item
            name="endDate"
            label="End date"
            dependencies={["startDate"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue("startDate");
                  if (!value || !start || !value.isBefore(start)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("End date cannot be before the start date"),
                  );
                },
              }),
            ]}
          >
            <DatePicker style={{ width: "100%" }} format={DISPLAY_FORMAT} />
          </Form.Item>

          <Form.Item name="defaultEnvironment" label="Default environment">
            <Select
              allowClear
              placeholder="Select an environment"
              options={envOptions.map((e) => ({ label: e, value: e }))}
            />
          </Form.Item>

          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Projects;
